import { cn } from "@/utils";
import { Check, X } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Tabela que compara opções critério a critério: "Caderninho × Planilha ×
 * Fidexa". Cada linha é um critério, cada coluna é uma opção.
 *
 * É `<table>` de verdade — `<caption>`, `<th scope="col">`, `<th scope="row">`
 * —, renderizada **uma vez só** e inteira no HTML. Crawler e LLM extraem
 * tabela HTML como dado estruturado; a mesma informação em `div` vira texto
 * corrido sem relação entre critério e opção.
 *
 * **No celular (< 768px) cada linha vira um cartão**: o critério em cima e,
 * abaixo, um par "opção — valor" por coluna. É CSS sobre a mesma marcação, não
 * uma segunda lista: o `DataList` desenha duas árvores (tabela e cartões), e
 * aqui isso faria o crawler e o LLM lerem a comparação duas vezes. Como trocar
 * o `display` de uma tabela apaga a semântica dela no Safari e no Chrome, cada
 * elemento declara o próprio papel (`role="table"`, `row`, `cell`…) — é o que
 * mantém a navegação por célula do leitor de tela no cartão.
 *
 * O rótulo da coluna no cartão sai de `data-label` por `::before`: fica fora
 * do texto da célula, então quem copia ou converte a tabela em Markdown não
 * recebe "PlanilhaSim".
 */

export interface ComparisonColumn<K extends string = string> {
  /** Chave da opção nas `cells` de cada linha. */
  key: K;
  /** Nome da opção, como o leitor fala: "Caderninho", "Fidexa". */
  label: string;
  /**
   * A opção em destaque (normalmente a Fidexa). Ganha fundo `ds-brand-50` e um
   * filete laranja no cabeçalho — sóbrio de propósito. Destaque **uma** coluna:
   * duas em destaque é nenhuma.
   */
  highlight?: boolean;
  /** Valores numéricos: dígitos tabulares e alinhamento à direita na tabela. */
  numeric?: boolean;
}

/** Conteúdo de uma célula: `true`/`false` viram "Sim"/"Não" com ícone. */
export type ComparisonCell = ReactNode | boolean;

export interface ComparisonRow<K extends string = string> {
  /** O critério: "Cobra pelo WhatsApp", "Custo por mês". */
  label: ReactNode;
  /** Um valor por coluna. Chave faltando vira "—". */
  cells: Partial<Record<K, ComparisonCell>>;
}

export interface ComparisonTableProps<K extends string = string> {
  /**
   * O que a tabela compara — vai no `<caption>`, que é o nome dela para leitor
   * de tela e para quem extrai a tabela. Obrigatório mesmo quando há um título
   * visível acima: aí use `captionVisibility="hidden"`.
   */
  caption: ReactNode;
  /**
   * `visible` (padrão) mostra a legenda acima da tabela. `hidden` a mantém só
   * para leitor de tela e crawler — use quando um `Heading` logo acima já diz
   * a mesma coisa.
   */
  captionVisibility?: "visible" | "hidden";
  /** Cabeçalho da coluna dos critérios. Padrão: "Critério". */
  rowHeaderLabel?: string;
  /** As opções comparadas, na ordem de leitura. Até 4 cabem a partir de 768px. */
  columns: ComparisonColumn<K>[];
  rows: ComparisonRow<K>[];
  className?: string;
}

const BooleanCell = ({ value }: { value: boolean }) =>
  value ? (
    <span className="inline-flex items-center gap-1.5">
      <Check
        aria-hidden="true"
        className="text-ds-success-700 size-5 shrink-0"
      />
      Sim
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5">
      <X aria-hidden="true" className="text-ds-ink-600 size-5 shrink-0" />
      Não
    </span>
  );

const renderCell = (value: ComparisonCell | undefined): ReactNode => {
  if (typeof value === "boolean") return <BooleanCell value={value} />;
  if (value === undefined || value === null || value === "") return "—";
  return value;
};

export const ComparisonTable = <K extends string>({
  caption,
  captionVisibility = "visible",
  rowHeaderLabel = "Critério",
  columns,
  rows,
  className,
}: ComparisonTableProps<K>) => (
  // A moldura só existe a partir de md. Ela rola de lado **dentro** de si se
  // alguém passar mais colunas do que cabem — a página nunca estica. Abaixo de
  // md não há moldura: cada cartão é a sua.
  <div
    className={cn(
      "md:border-ds-border md:bg-ds-surface md:rounded-ds-lg md:overflow-x-auto md:border",
      className
    )}
  >
    <table
      role="table"
      className="font-ds-sans text-ds-body block w-full md:table md:border-collapse"
    >
      <caption
        className={cn(
          captionVisibility === "hidden"
            ? "sr-only"
            : "text-ds-ink-900 block pb-3 text-left font-semibold md:table-caption md:px-4 md:pt-4"
        )}
      >
        {caption}
      </caption>

      {/* No cartão o cabeçalho sai da tela, mas não do leitor de tela: os
          papéis de coluna continuam ligando cada valor à sua opção. */}
      <thead
        role="rowgroup"
        className="sr-only md:not-sr-only md:table-header-group"
      >
        <tr role="row" className="md:table-row">
          <th
            role="columnheader"
            scope="col"
            className="bg-ds-ink-50 text-ds-small text-ds-ink-700 border-ds-border px-4 py-3 text-left align-bottom font-semibold md:border-b"
          >
            {rowHeaderLabel}
          </th>
          {columns.map(column => (
            <th
              key={column.key}
              role="columnheader"
              scope="col"
              className={cn(
                "text-ds-small border-ds-border px-4 py-3 align-bottom font-semibold md:border-b",
                column.numeric ? "text-right" : "text-left",
                column.highlight
                  ? "bg-ds-brand-50 text-ds-brand-700 border-t-ds-brand-500 md:border-t-4"
                  : "bg-ds-ink-50 text-ds-ink-700"
              )}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>

      <tbody role="rowgroup" className="flex flex-col gap-3 md:table-row-group">
        {rows.map((row, index) => (
          // Chave pelo índice: o critério pode ser ReactNode, e a tabela é
          // conteúdo estático — nunca reordena nem remove linha.
          // Em md o cartão vira linha: sobra só o filete de baixo, que é o
          // divisor entre critérios (a moldura já fecha a última).
          <tr
            key={index}
            role="row"
            className="border-ds-border bg-ds-surface rounded-ds-lg flex flex-col border p-4 md:table-row md:rounded-none md:border-x-0 md:border-t-0 md:bg-transparent md:p-0 md:last:border-b-0"
          >
            <th
              role="rowheader"
              scope="row"
              className="text-ds-ink-900 block pb-2 text-left align-top font-semibold md:table-cell md:px-4 md:py-3"
            >
              {row.label}
            </th>
            {columns.map(column => (
              <td
                key={column.key}
                role="cell"
                data-label={column.label}
                className={cn(
                  // Duas colunas iguais no cartão: com `flex` e rótulo
                  // `shrink-0`, um rótulo longo ("Planilha e WhatsApp do
                  // celular") espremia o valor até quebrar letra por letra.
                  "text-ds-ink-800 border-ds-border grid grid-cols-2 items-baseline gap-4 border-t py-2 text-right",
                  // Rótulo da opção no cartão. Sai em md, onde o cabeçalho
                  // aparece. Tinta `ink-600`: o piso do texto (5,5:1).
                  "before:text-ds-small before:text-ds-ink-600 before:text-left before:font-medium before:content-[attr(data-label)]",
                  "md:table-cell md:border-t-0 md:px-4 md:py-3 md:align-top md:before:content-none",
                  column.numeric ? "ds-numeric md:text-right" : "md:text-left",
                  column.highlight && "bg-ds-brand-50 -mx-2 px-2 md:mx-0"
                )}
              >
                <span className="min-w-0 break-words">
                  {renderCell(row.cells[column.key])}
                </span>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
